// src/services/desktopNotificationService.ts
// Desktop (OS) notifications and the notification sound for bell alerts.
// Port of the legacy App/Services/DesktopNotificationsService.js.
//
// Unlike the legacy version — where the sound only played as a side effect of
// creating a desktop notification — the two settings are independent here, the
// same way the chat handles them (see packages/chat MainChat.tsx). Each one is
// gated by its own localStorage flag, shared with the chat:
//   C2_messageNotification -> desktop notification
//   C2_soundNotification   -> sound

const AUDIO_ELEMENT_ID = 'notificationBloop';
const NOTIFICATION_TAG = 'corgee-alert';
const AUTO_CLOSE_MS = 8000;
const BODY_MAX_LENGTH = 50;

/**
 * Payload broadcast on socket:client:notification (NotificationRequestVM) and on
 * socket:client:notificationProjectChannel, which uses Action/Body instead.
 */
export interface DesktopNotificationPayload
{
  Title?: string;
  Title4Desktop?: string;
  Action4Desktop?: string;
  Action?: string;
  Body?: string;
  UpdateType?: string;
}

/**
 * Reads one of the shared notification flags. When the key was never written on
 * this device the app-wide default applies, the same way the chat seeds them in
 * AuthService.js.
 */
export function readNotificationFlag(key: string, fallback: boolean): boolean
{
  try
  {
    const stored = localStorage.getItem(key);
    return stored === null ? fallback : stored === 'true';
  }
  catch
  {
    // Storage blocked (private mode): treat the setting as off
    return false;
  }
}

/** Plays the notification sound when the user has it enabled. */
export function playNotificationSound(): void
{
  if (!readNotificationFlag('C2_soundNotification', true)) return;

  const audio = document.getElementById(AUDIO_ELEMENT_ID) as HTMLAudioElement | null;
  if (!audio) return;

  // Restart so bursts are audible instead of being swallowed by the previous play
  audio.currentTime = 0;
  audio.play().catch(() =>
  {
    // Browsers block autoplay until the user has interacted with the page
  });
}

/**
 * Title4Desktop is an HTML fragment. The legacy code read the "#labela" and
 * "#spana" nodes to build "<what happened> by <who>"; fall back to the plain
 * text of the fragment when the template does not carry those ids.
 */
function parseBody(html: string): { text: string; href: string | null }
{
  const container = document.createElement('div');
  container.innerHTML = html;

  const label = container.querySelector('#labela');
  const author = container.querySelector('#spana');

  let text: string;
  if (label)
  {
    const authorText = author?.textContent?.trim();
    text = authorText
      ? `${label.textContent?.trim()} by ${authorText}`
      : (label.textContent?.trim() ?? '');
  }
  else
  {
    text = container.textContent?.trim() ?? '';
  }

  if (text.length > BODY_MAX_LENGTH)
  {
    text = `${text.slice(0, BODY_MAX_LENGTH - 3)}...`;
  }

  return { text, href: label?.getAttribute('href') ?? null };
}

/**
 * Raises an OS notification for a bell alert when the user has them enabled
 * and the browser granted the permission. Returns the notification, if any.
 */
export function showDesktopNotification(payload: DesktopNotificationPayload): Notification | null
{
  // Defaulting to on is safe: the permission check below is the real gate
  if (!readNotificationFlag('C2_messageNotification', true)) return null;
  if (!('Notification' in window) || Notification.permission !== 'granted') return null;

  const { text, href } = parseBody(payload.Title4Desktop ?? payload.Body ?? payload.Title ?? '');
  const title = payload.Action4Desktop || payload.Action || 'Corgee';
  if (!text) return null;

  try
  {
    const notification = new Notification(title, {
      body: text,
      tag: NOTIFICATION_TAG,
      icon: `${import.meta.env.BASE_URL}Images/corgee-dog-circle.png`,
      dir: 'ltr',
      // The app plays its own sound, gated by its own setting
      silent: true,
    });

    setTimeout(() => notification.close(), AUTO_CLOSE_MS);

    notification.onclick = () =>
    {
      if (href) window.location.href = href;
      window.focus();
      notification.close();
    };

    return notification;
  }
  catch
  {
    // Some browsers reject the constructor outside a service worker
    return null;
  }
}

/** Sound + desktop notification for one bell alert, each honouring its own setting. */
export function notifyBellAlert(payload: DesktopNotificationPayload): void
{
  playNotificationSound();
  showDesktopNotification(payload);
}
